<main class="canvas-wide">
  <div class="page-head">
    <div class="eyebrow">MedOne staff view</div>
    <h1>Insights</h1>
    @if (selectedRelease(); as r) {
      <p class="page-sub">{{ r.releaseName }}</p>
    }
  </div>

  <div class="tabs">
    <button type="button" class="tab" [class.active]="tab() === 'overview'" (click)="setTab('overview')">Overview</button>
    <button type="button" class="tab" [class.active]="tab() === 'testers'" (click)="setTab('testers')">Testers</button>
    <button type="button" class="tab" [class.active]="tab() === 'failures'" (click)="setTab('failures')">Failures</button>
    <button type="button" class="tab" [class.active]="tab() === 'audit'" (click)="setTab('audit')">Audit log</button>
  </div>

  @if (isLoading()) {
    <div class="insights-loading">
      <span class="spinner" aria-hidden="true"></span>
      <span>Loading insights…</span>
    </div>
  } @else if (loadError()) {
    <div class="error-state">
      <div class="error-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>
      <h4>We couldn't load insights</h4>
      <p>The server didn't respond. Check your connection and try again.</p>
      <button type="button" class="btn btn-primary btn-sm" (click)="load()">Try again</button>
    </div>
  } @else if (!selectedRelease()) {
    <div class="empty-state">
      <h4>No active release</h4>
      <p>Insights appear once a release is active.</p>
      <a class="btn btn-ghost btn-sm" routerLink="/releases">Go to releases</a>
    </div>
  } @else {
    @switch (tab()) {
      <!-- ===================== OVERVIEW ===================== -->
      @case ('overview') {
        @if (overview(); as o) {
          <div class="kpi-grid">
            <div class="kpi">
              <div class="kpi-label">Overall progress</div>
              <div class="kpi-value">{{ o.overallProgressPct }}%</div>
              <span class="kpi-delta">{{ o.testsComplete }} of {{ o.testsTotal }} tests complete</span>
            </div>
            <div class="kpi">
              <div class="kpi-label">Pass rate</div>
              <div class="kpi-value">{{ o.passRatePct | number: '1.0-1' }}%</div>
              <span class="kpi-delta">{{ o.passed }} passed of {{ o.run }} run</span>
            </div>
            <div class="kpi">
              <div class="kpi-label">Active testers</div>
              <div class="kpi-value">
                {{ o.activeTesters }} <span class="kpi-of">of {{ o.totalTesters }}</span>
              </div>
              <span class="kpi-delta">{{ o.unassignedPlans }} unassigned plans</span>
            </div>
            <div class="kpi">
              <div class="kpi-label">Open failures</div>
              <div class="kpi-value fail">{{ o.openFailures }}</div>
            </div>
          </div>

          <div class="dash-grid">
            <!-- All failures -->
            <section class="panel">
              <div class="panel-header">
                <h3>All failures</h3>
                <span class="panel-meta">{{ o.openFailures }} open</span>
              </div>
              <div class="panel-body">
                @for (f of o.allFailures; track f.testCaseId) {
                  <div class="fail-row">
                    <div class="icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M6 6l12 12M6 18L18 6" /></svg>
                    </div>
                    <div>
                      <div class="name">{{ f.testCaseName }}</div>
                      <div class="meta">
                        {{ f.planName }}@if (f.severity) { · {{ f.severity | lowercase }} severity }
                      </div>
                    </div>
                  </div>
                } @empty {
                  <div class="empty-row">No open failures. Nice.</div>
                }
              </div>
            </section>

            <!-- Tester activity -->
            <section class="panel">
              <div class="panel-header">
                <h3>Tester activity</h3>
                <button type="button" class="btn-link" (click)="setTab('testers')">All testers →</button>
              </div>
              <div class="panel-body flush">
                @for (t of o.testerActivity; track t.userId) {
                  <div class="activity-tester">
                    <div class="activity-tester-head">
                      <span class="tester-avatar">{{ initials(t.firstName, t.lastName) }}</span>
                      <span class="name">{{ t.firstName }} {{ t.lastName }}</span>
                      <span class="totals"><strong>{{ t.completedCount }}</strong> tests completed</span>
                    </div>
                    @for (c of t.recentCompleted; track $index) {
                      <div class="completed-test">
                        <svg class="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        <span>{{ c.testCaseName }} <span class="plan-tag">· {{ c.planName }}</span></span>
                      </div>
                    }
                    @if (t.completedCount > t.recentCompleted.length) {
                      <div class="completed-test more">+ {{ t.completedCount - t.recentCompleted.length }} more</div>
                    }
                  </div>
                } @empty {
                  <div class="empty-row">No tester activity yet.</div>
                }
              </div>
            </section>
          </div>
        }
      }

      <!-- ===================== TESTERS ===================== -->
      @case ('testers') {
        <div class="toolbar">
          <div class="search-input">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
            <input type="text" placeholder="Search testers" [value]="search()" (input)="search.set($any($event.target).value)" aria-label="Search testers" />
            @if (search()) {
              <button type="button" class="search-clear" aria-label="Clear search" (click)="search.set('')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            }
          </div>
          <div class="chip-group">
            @for (c of testerChips; track c) {
              <button type="button" class="chip" [class.active]="testerFilter() === c" (click)="testerFilter.set(c)">
                {{ c }} <span class="count">{{ testerCounts()[c] }}</span>
              </button>
            }
          </div>
          <button class="btn btn-ghost export-btn" type="button" (click)="exportCsv()">Export CSV</button>
        </div>

        @for (t of filteredTesters(); track t.userId) {
          <div class="tester-card">
            <div class="tester-card-head">
              <span class="tester-avatar lg">{{ initials(t.firstName, t.lastName) }}</span>
              <div>
                <div class="name">{{ t.firstName }} {{ t.lastName }}</div>
                @if (t.currentPlanName) {
                  <div class="sub">Currently on {{ t.currentPlanName }} · Test {{ t.currentTestNumber }} of {{ t.currentTestTotal }}</div>
                } @else {
                  <div class="sub">No plan in progress</div>
                }
              </div>
              <div class="right">
                <div class="stat-pair"><div class="lbl">Completed</div><div class="val">{{ t.completedCount }}</div></div>
                <div class="stat-pair">
                  <div class="lbl">Pass rate</div>
                  <div class="val" [class.pass]="t.passRatePct >= 90" [class.fail]="t.passRatePct < 80">{{ t.passRatePct | number: '1.0-1' }}%</div>
                </div>
                <div class="stat-pair">
                  <div class="lbl">Failures filed</div>
                  <div class="val" [class.fail]="t.failuresFiled > 0">{{ t.failuresFiled }}</div>
                </div>
              </div>
            </div>
            @for (p of t.plans; track p.planName) {
              <div class="plan-strip">
                <div class="pname">
                  {{ p.planName }}
                  @if (p.isCurrent) { <span class="current-pill">current</span> }
                </div>
                <div class="pmini-bar">
                  <div class="pf" [class.full]="p.numberOfPassingTests === p.numberOfTests && p.numberOfTests > 0" [style.width.%]="planPct(p.numberOfPassingTests, p.numberOfTests)"></div>
                  @if (p.numberOfFailingTests > 0) {
                    <div class="ff" [style.left.%]="planPct(p.numberOfPassingTests, p.numberOfTests)" [style.width.%]="failPct(p.numberOfFailingTests, p.numberOfTests)"></div>
                  }
                </div>
                <div class="pcount">{{ p.numberOfPassingTests }} / {{ p.numberOfTests }}</div>
              </div>
            }
          </div>
        } @empty {
          <div class="empty-state">
            <h4>No testers match</h4>
            <p>Try a different search or filter.</p>
            <button type="button" class="btn btn-ghost btn-sm" (click)="resetFilters()">Clear filters</button>
          </div>
        }
      }

      <!-- ===================== FAILURES ===================== -->
      @case ('failures') {
        <div class="toolbar">
          <div class="search-input">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
            <input type="text" placeholder="Search failures" [value]="search()" (input)="search.set($any($event.target).value)" aria-label="Search failures" />
            @if (search()) {
              <button type="button" class="search-clear" aria-label="Clear search" (click)="search.set('')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            }
          </div>
          <div class="chip-group">
            @for (c of severityChips; track c) {
              <button type="button" class="chip" [class.active]="severityFilter() === c" (click)="severityFilter.set(c)">
                {{ c }} <span class="count">{{ severityCounts()[c] }}</span>
              </button>
            }
          </div>
          <button class="btn btn-ghost export-btn" type="button" (click)="exportCsv()">Export CSV</button>
        </div>

        @if (filteredFailures().length === 0) {
          <div class="empty-state">
            <div class="empty-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M20 6 9 17l-5-5" /></svg>
            </div>
            <h4>No failures to show</h4>
            <p>Nothing matches those filters.</p>
            <button type="button" class="btn btn-ghost btn-sm" (click)="resetFilters()">Clear filters</button>
          </div>
        } @else {
          <div class="fail-table">
            <div class="fail-thead">
              <div>Severity</div><div>Failure</div><div>Plan</div><div>Reporter</div><div>Status</div><div>Filed</div>
            </div>
            @for (f of filteredFailures(); track f.testCaseId) {
              <div class="fail-tr">
                <div><span class="sev-pill {{ sevClass(f.severity) }}">{{ f.severity ?? 'Unspecified' }}</span></div>
                <div class="title">{{ f.testCaseName }}</div>
                <div class="plan">{{ f.planName }}</div>
                <div class="reporter">
                  <span class="tester-avatar sm">{{ initialsFromName(f.reporterName) }}</span>
                  <span>{{ f.reporterName }}</span>
                </div>
                <div><span class="status-pill-tiny {{ f.status | lowercase }}">{{ f.status }}</span></div>
                <div class="num">{{ f.filedOnUtc | date: 'MMM d' }}</div>
              </div>
            }
          </div>
        }
      }

      <!-- ===================== AUDIT ===================== -->
      @case ('audit') {
        <div class="toolbar">
          <div class="search-input">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
            <input type="text" placeholder="Search log" [value]="search()" (input)="search.set($any($event.target).value)" aria-label="Search log" />
            @if (search()) {
              <button type="button" class="search-clear" aria-label="Clear search" (click)="search.set('')">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            }
          </div>
          <div class="chip-group">
            @for (c of auditChips(); track c) {
              <button type="button" class="chip" [class.active]="auditFilter() === c" (click)="auditFilter.set(c)">{{ c }} ({{ auditCounts()[c] }})</button>
            }
          </div>
          <button class="btn btn-ghost export-btn" type="button" (click)="exportCsv()">Export CSV</button>
        </div>

        @if (filteredAudit().length === 0) {
          <div class="empty-state">
            <h4>Nothing logged yet</h4>
            <p>Test results will appear here as testers work.</p>
          </div>
        } @else {
          <div class="audit-list">
            @for (day of auditDays(); track day.label) {
              <div class="audit-day-header">{{ day.label }}</div>
              @for (e of day.entries; track $index) {
                <div class="audit-row">
                  <div class="time">{{ e.timeUtc | date: 'shortTime' }}</div>
                  <span class="tester-avatar sm">{{ initialsFromName(e.userName) }}</span>
                  <div class="action">
                    @if (e.actionType === 'Passed') {
                      <!-- &ngsp; is a real space Angular will not collapse. Without it the
                           detail runs into the text before it, in the accessibility tree
                           as well as on screen: "Searchto Marcus Chen". -->
                      {{ e.userName }} marked <b>{{ e.itemName }}</b> as <span class="tag-passed">Passed</span>&ngsp;
                      @if (e.detail) { <span class="detail">"{{ e.detail }}"</span> }
                    } @else if (e.actionType === 'Failed') {
                      {{ e.userName }} marked <b>{{ e.itemName }}</b> as
                      <span class="tag-failed">Failed</span>@if (e.severity) { ({{ e.severity }}) }&ngsp;
                      @if (e.detail) { <span class="detail">"{{ e.detail }}"</span> }
                    } @else {
                      <!-- Reads as a sentence: "Kelsey Lee reassigned Support Tool to Bob
                           Jones". The detail is the value the change landed on, and is
                           absent when the action already says it ("hid", "skipped"). It is
                           not quoted the way a tester's comment is. -->
                      {{ e.userName }} {{ actionVerb(e.actionType) }} <b>{{ e.itemName }}</b>&ngsp;
                      @if (e.detail) { <span class="detail">to {{ e.detail }}</span> }
                    }
                  </div>
                  <div class="meta">{{ e.planName }}</div>
                </div>
              }
            }
          </div>
        }
      }
    }
  }
</main>
